import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ClipboardCheck, GripVertical, Home, PlaneLanding } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { BRAND_COLORS } from "../lib/bookingStatus";

const SUGGESTIONS = {
  departure: [
    "Alle Fenster und Türen geschlossen",
    "Alarmanlage aktiviert",
    "Kaffeemaschine / Elektrogeräte ausgeschaltet",
    "Müll entsorgt",
    "Kühlschrank ausgeräumt und abgetaut",
    "Heizung auf Winter-/Sommermodus umgestellt",
    "Wasserzufuhr abgestellt",
    "Rollläden heruntergelassen",
  ],
  arrival: [
    "Heizung auf Wohntemperatur eingestellt",
    "Rollläden geöffnet · Räume gelüftet",
    "Einkauf bereitgestellt · Kühlschrank gefüllt",
    "Wasser aufgedreht · Boiler eingeschaltet",
    "Alarmanlage deaktiviert",
    "Blumen · Willkommensgruß platziert",
    "Sauna vorgewärmt",
    "Kamin vorbereitet · Holz gestapelt",
  ],
};

const TAB_META = {
  departure: { label: "Abreise", icon: Home,          field: "items",         placeholder: "z. B. Fenster geschlossen" },
  arrival:   { label: "Anreise", icon: PlaneLanding,  field: "arrival_items", placeholder: "z. B. Heizung auf 20 °C" },
};

export default function TeamChecklists() {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [tab, setTab] = useState("departure");
  const [items, setItems] = useState([]);
  const [arrivalItems, setArrivalItems] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/team/customers");
        setCustomers(data);
        if (data[0]) setCustomerId(data[0].id);
      } catch (e) { console.error("customers load failed", e); }
    })();
  }, []);

  useEffect(() => {
    if (!customerId) return;
    (async () => {
      try {
        const { data } = await api.get(`/team/customers/${customerId}/checklist`);
        setItems(data.items || []);
        setArrivalItems(data.arrival_items || []);
      } catch (err) {
        toast.error(formatErr(err.response?.data?.detail) || err.message);
      }
    })();
  }, [customerId]);

  const current = tab === "departure" ? items : arrivalItems;
  const setCurrent = tab === "departure" ? setItems : setArrivalItems;
  const suggestions = SUGGESTIONS[tab];
  const meta = TAB_META[tab];

  const addItem = (label = "") => setCurrent((cur) => [...cur, { key: `k-${Date.now()}-${cur.length}`, label }]);
  const updateItem = (i, label) => setCurrent((cur) => cur.map((it, idx) => idx === i ? { ...it, label } : it));
  const removeItem = (i) => setCurrent((cur) => cur.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        [meta.field]: current.filter((it) => (it.label || "").trim()),
      };
      const { data } = await api.put(`/team/customers/${customerId}/checklist`, payload);
      setItems(data.items || []);
      setArrivalItems(data.arrival_items || []);
      toast.success(`${meta.label}-Checkliste gespeichert. Kunde sieht sie sofort in der App.`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const unusedSuggestions = useMemo(
    () => suggestions.filter((s) => !current.some((it) => it.label === s)),
    [current, suggestions],
  );
  const customer = customers.find((c) => c.id === customerId);

  return (
    <div className="max-w-[900px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="team-checklists">
      <div className="mb-8">
        <div className="label-caps mb-3">Weissblom Team</div>
        <h1 className="font-display text-4xl md:text-5xl text-navy">Kunden-Checklisten</h1>
        <p className="text-[14px] text-navy-dark/70 mt-3 max-w-2xl">
          Pflegen Sie pro Kunde eine individuelle <strong>Abreise-</strong> und <strong>Anreise-Checkliste</strong>. Die Punkte erscheinen automatisch in der Kunden-App unter „Ab-/Anreise vorbereiten".
        </p>
      </div>

      <div className="bg-white border border-navy/10 p-5 md:p-6 mb-6">
        <label htmlFor="team-checklist-customer" className="label-caps block mb-2">Kunde</label>
        <select id="team-checklist-customer" data-testid="team-checklist-customer"
          value={customerId} onChange={(e) => setCustomerId(e.target.value)}
          className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark bg-white">
          {customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.email}</option>)}
        </select>
      </div>

      {/* Tab switcher */}
      <div className="flex items-center gap-0 mb-0 border-b border-navy/10" data-testid="team-checklist-tabs">
        {Object.entries(TAB_META).map(([key, m]) => {
          const Icon = m.icon;
          const active = tab === key;
          const count = (key === "departure" ? items : arrivalItems).length;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              data-testid={`team-checklist-tab-${key}`}
              className={`inline-flex items-center gap-2 px-5 py-3 text-[12px] tracking-[0.14em] uppercase transition-colors border-b-2 -mb-px ${active ? "text-navy border-navy" : "text-navy-dark/50 border-transparent hover:text-navy"}`}
            >
              <Icon className="w-4 h-4" strokeWidth={1.5} />
              {m.label}
              {count > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 text-[10px] rounded-full" style={{ background: active ? BRAND_COLORS.navy : "#E7E1D3", color: active ? BRAND_COLORS.ivory : BRAND_COLORS.navy }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <section className="bg-white border border-navy/10 border-t-0 p-5 md:p-6" data-testid="team-checklist-editor">
        <div className="flex items-center gap-2 mb-4">
          <ClipboardCheck className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.4} />
          <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>
            {meta.label}-Checkliste{customer ? ` für ${customer.name || customer.email}` : ""}
          </div>
        </div>

        {current.length === 0 ? (
          <div className="text-[13px] text-navy-dark/60 italic mb-4" data-testid="team-checklist-empty">
            Noch keine Punkte hinterlegt. Fügen Sie welche hinzu — bis dahin sieht der Kunde die Standardliste.
          </div>
        ) : (
          <ul className="space-y-2 mb-4" data-testid="team-checklist-items">
            {current.map((it, i) => (
              <li key={it.key} className="flex items-center gap-2" data-testid={`team-checklist-item-${i}`}>
                <GripVertical className="w-4 h-4 text-navy-dark/30 shrink-0" strokeWidth={1.4} />
                <input value={it.label} onChange={(e) => updateItem(i, e.target.value)}
                  placeholder={meta.placeholder}
                  data-testid={`team-checklist-input-${i}`}
                  className="flex-1 border border-navy/15 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy-dark" />
                <button onClick={() => removeItem(i)} title="Entfernen"
                  data-testid={`team-checklist-remove-${i}`}
                  className="text-navy-dark/50 hover:text-red-600 p-1.5">
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </li>
            ))}
          </ul>
        )}

        <button onClick={() => addItem("")}
          data-testid="team-checklist-add"
          className="inline-flex items-center gap-2 text-[11px] tracking-wider uppercase border border-navy/25 hover:border-gold text-navy px-3 py-2">
          <Plus className="w-4 h-4" strokeWidth={1.5} /> Punkt hinzufügen
        </button>

        {unusedSuggestions.length > 0 && (
          <div className="mt-6 pt-5 border-t border-navy/10">
            <div className="label-caps mb-2 text-navy-dark/60">Vorschläge</div>
            <div className="flex flex-wrap gap-2">
              {unusedSuggestions.map((s) => (
                <button key={s} onClick={() => addItem(s)}
                  data-testid={`team-checklist-suggest-${s.replace(/[^a-z0-9]+/gi, '-').toLowerCase().slice(0, 30)}`}
                  className="text-[11.5px] text-navy-dark border border-navy/15 hover:border-gold px-3 py-1.5">
                  + {s}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 pt-5 border-t border-navy/10 flex justify-end">
          <button onClick={save} disabled={busy}
            data-testid="team-checklist-save"
            className="text-ivory px-6 py-3 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-60"
            style={{ background: BRAND_COLORS.navy }}>
            {busy ? "Wird gespeichert…" : `${meta.label}-Checkliste speichern`}
          </button>
        </div>
      </section>
    </div>
  );
}
